// src/services/security.js - Security & Cryptographic Utilities
// Enterprise-grade security for license tier verification and data integrity

/**
 * Generate device fingerprint for tier binding
 * Combines multiple browser characteristics to create a unique identifier
 * Note: This is client-side only and can be spoofed, but adds significant friction
 */
export const generateDeviceFingerprint = async () => {
  try {
    const components = [];

    // Screen resolution (stable)
    components.push(`${window.screen.width}x${window.screen.height}x${window.screen.colorDepth}`);

    // Timezone
    components.push(Intl.DateTimeFormat().resolvedOptions().timeZone);

    // Language
    components.push(navigator.language);

    // Platform
    components.push(navigator.platform);

    // Hardware concurrency (CPU cores)
    components.push(navigator.hardwareConcurrency || 'unknown');

    // User agent (less stable, but useful)
    components.push(navigator.userAgent);

    // Canvas fingerprint (more stable than user agent)
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.textBaseline = 'top';
      ctx.font = '14px Arial';
      ctx.fillStyle = '#f60';
      ctx.fillRect(125, 1, 62, 20);
      ctx.fillStyle = '#069';
      ctx.fillText('ThryveLoop', 2, 15);
      components.push(canvas.toDataURL());
    }

    // WebGL fingerprint
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    if (gl) {
      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
      if (debugInfo) {
        components.push(gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL));
        components.push(gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL));
      }
    }

    // Combine all components and hash
    const fingerprint = await hashString(components.join('|||'));
    return fingerprint;
  } catch (error) {
    console.error('Error generating device fingerprint:', error);
    // Fallback to timestamp-based ID (less secure, but better than nothing)
    return await hashString(`fallback-${Date.now()}-${Math.random()}`);
  }
};

/**
 * Hash a string using SHA-256 (via Web Crypto API)
 * @param {string} message - String to hash
 * @returns {Promise<string>} Hex-encoded hash
 */
export const hashString = async (message) => {
  try {
    const encoder = new TextEncoder();
    const data = encoder.encode(message);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    return hashHex;
  } catch (error) {
    console.error('Error hashing string:', error);
    // Fallback to simple hash (less secure, but works without crypto API)
    let hash = 0;
    for (let i = 0; i < message.length; i++) {
      const char = message.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash).toString(16);
  }
};

/**
 * Generate HMAC-SHA256 signature for data integrity
 * @param {string} message - Data to sign
 * @param {string} secret - Secret key (device fingerprint)
 * @returns {Promise<string>} Hex-encoded signature
 */
export const generateHMAC = async (message, secret) => {
  try {
    const encoder = new TextEncoder();

    // Import secret as crypto key
    const keyData = encoder.encode(secret);
    const key = await crypto.subtle.importKey(
      'raw',
      keyData,
      { name: 'HMAC', hash: 'SHA-256' },
      false,
      ['sign']
    );

    // Sign the message
    const signature = await crypto.subtle.sign(
      'HMAC',
      key,
      encoder.encode(message)
    );

    // Convert to hex
    const signatureArray = Array.from(new Uint8Array(signature));
    const signatureHex = signatureArray.map(b => b.toString(16).padStart(2, '0')).join('');
    return signatureHex;
  } catch (error) {
    console.error('Error generating HMAC:', error);
    // Fallback to simple hash-based MAC (less secure)
    return await hashString(`${message}${secret}`);
  }
};

/**
 * Verify HMAC signature
 * @param {string} message - Original message
 * @param {string} signature - Signature to verify
 * @param {string} secret - Secret key
 * @returns {Promise<boolean>}
 */
export const verifyHMAC = async (message, signature, secret) => {
  try {
    const expectedSignature = await generateHMAC(message, secret);
    return signature === expectedSignature;
  } catch (error) {
    console.error('Error verifying HMAC:', error);
    return false;
  }
};

/**
 * Secure localStorage write with integrity protection
 * @param {string} key - Storage key
 * @param {any} value - Value to store
 * @param {string} secret - Secret for HMAC (device fingerprint)
 */
export const secureStorageWrite = async (key, value, secret) => {
  try {
    const data = JSON.stringify(value);
    const signature = await generateHMAC(data, secret);

    const envelope = {
      data: data,
      signature: signature,
      timestamp: Date.now(),
    };

    localStorage.setItem(key, JSON.stringify(envelope));
    return true;
  } catch (error) {
    console.error('Error writing secure storage:', error);
    return false;
  }
};

/**
 * Secure localStorage read with integrity verification
 * @param {string} key - Storage key
 * @param {string} secret - Secret for HMAC verification
 * @returns {Promise<{value: any, valid: boolean, tampered: boolean}>}
 */
export const secureStorageRead = async (key, secret) => {
  try {
    const stored = localStorage.getItem(key);
    if (!stored) {
      return { value: null, valid: false, tampered: false };
    }

    const envelope = JSON.parse(stored);

    // Check envelope structure
    if (!envelope.data || !envelope.signature) {
      console.warn(`  Invalid storage envelope for key: ${key}`);
      return { value: null, valid: false, tampered: true };
    }

    // Verify signature
    const valid = await verifyHMAC(envelope.data, envelope.signature, secret);

    if (!valid) {
      console.error(` TAMPERING DETECTED: Invalid signature for key: ${key}`);
      return { value: null, valid: false, tampered: true };
    }

    // Parse and return data
    const value = JSON.parse(envelope.data);
    return { value, valid: true, tampered: false };
  } catch (error) {
    console.error('Error reading secure storage:', error);
    return { value: null, valid: false, tampered: false };
  }
};

/**
 * Check if running in production environment
 * @returns {boolean}
 */
export const isProduction = () => {
  // Check multiple indicators of production environment
  const nodeEnv = process.env.NODE_ENV === 'production';
  const hostname = typeof window !== 'undefined' && window.location.hostname;
  const isProductionDomain = hostname === 'thryveloop.com' || hostname === 'www.thryveloop.com';

  return nodeEnv || isProductionDomain;
};

/**
 * Check if debug mode is allowed
 * Debug mode should ONLY work in development environments
 * @returns {boolean}
 */
export const isDebugModeAllowed = () => {
  if (isProduction()) {
    if (!debugModeBlockedWarned) {
      debugModeBlockedWarned = true;
      console.warn(' DEBUG MODE BLOCKED: Debug features are disabled in production');
    }
    return false;
  }

  // Only allow debug mode in development
  return process.env.NODE_ENV === 'development' ||
         (typeof window !== 'undefined' &&
          (window.location.hostname === 'localhost' ||
           window.location.hostname === '127.0.0.1' ||
           window.location.hostname.endsWith('.local')));
};

/**
 * Sanitize and validate tier value
 * @param {string} tier - Tier to validate
 * @returns {string} Valid tier or 'free'
 */
export const sanitizeTier = (tier) => {
  const validTiers = ['free', 'pro', 'local', 'lifestyle', 'explorer', 'believer', 'guardian', 'lifetime'];
  if (!tier || typeof tier !== 'string') {
    return 'free';
  }

  const normalized = tier.trim().toLowerCase();
  if (normalized === 'lifestyle') {
    return 'lifetime';
  }
  if (!validTiers.includes(normalized)) {
    return 'free';
  }

  return normalized;
};

/**
 * Generate secure random string (for nonces, etc.)
 * @param {number} length - Length of random string
 * @returns {string}
 */
export const generateSecureRandom = (length = 32) => {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
};

/**
 * Rate limiter for API calls
 * Prevents brute force attacks on license validation
 */
class RateLimiter {
  constructor(maxAttempts = 5, windowMs = 60000) {
    this.maxAttempts = maxAttempts;
    this.windowMs = windowMs;
    this.attempts = new Map();
  }

  /**
   * Check if action is allowed
   * @param {string} key - Identifier (e.g., IP, license key)
   * @returns {boolean}
   */
  isAllowed(key) {
    const now = Date.now();
    const attempts = this.attempts.get(key) || [];

    // Remove old attempts outside the window
    const recentAttempts = attempts.filter(time => now - time < this.windowMs);

    if (recentAttempts.length >= this.maxAttempts) {
      return false;
    }

    // Record this attempt
    recentAttempts.push(now);
    this.attempts.set(key, recentAttempts);

    return true;
  }

  /**
   * Reset attempts for a key
   * @param {string} key
   */
  reset(key) {
    this.attempts.delete(key);
  }
}

// Export singleton rate limiter for license validation
export const licenseValidationRateLimiter = new RateLimiter(5, 60000); // 5 attempts per minute

export default {
  generateDeviceFingerprint,
  hashString,
  generateHMAC,
  verifyHMAC,
  secureStorageWrite,
  secureStorageRead,
  isProduction,
  isDebugModeAllowed,
  sanitizeTier,
  generateSecureRandom,
  licenseValidationRateLimiter,
};
let debugModeBlockedWarned = false;
